import { MessageOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import BaseButton from 'components/atoms/baseButton';
import DraggableModal from 'components/organisms/draggableModal';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter, groupArrayByFieldname } from 'infrastructure/functions';
import { useEffect, useReducer, type FC } from 'react';

import ReadingCell from '../reading-cell';
import ThresholdCell from '../threshold-cell';
import CommentList from './comment-list';
import s from './styles.module.scss';

import type { ReadingStatus, ReadingType } from 'infrastructure/enums';
import type { IPatientThresholds } from 'infrastructure/interfaces';
import type {
  IActivityComments,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { Nullable } from 'infrastructure/types';

interface CommentsCellProps {
  comments: IActivityComments[];
  patientGuid: string;
  title: string;
  subtitle?: string;
  values: Value;
  statuses: TStatuses;
  readingType: ReadingType;
  globalStatus: ReadingStatus;
  thresholds: IPatientThresholds;
  thresholdName?: Nullable<TThresholdName>;
  page: number;
}

const CommentsCell: FC<CommentsCellProps> = ({
  comments,
  patientGuid,
  title,
  subtitle,
  values,
  statuses,
  readingType,
  globalStatus,
  thresholds,
  thresholdName,
  page,
}) => {
  const [open, toggle] = useReducer((v) => !v, false);

  const sortedCommentsByDate = comments.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;

    return 0;
  });

  const commentsCount = sortedCommentsByDate.length;
  const commentWithFormattedDate = sortedCommentsByDate.map((comment) => ({
    ...comment,
    formattedCreateAt: DateFormatter({
      date: comment.createdAt,
      format: DateFormats['MM/dd/yyyy'],
    }),
  }));
  const groupedComments = groupArrayByFieldname(
    commentWithFormattedDate,
    'formattedCreateAt',
  );

  useEffect(() => {
    if (open) toggle();
  }, [page]);

  return (
    <div>
      <Badge
        offset={[-2, 0]}
        size="small"
        count={commentsCount}
        className={s['comment-badge']}
      >
        <BaseButton
          type="secondary"
          className={s['comment-btn']}
          onClick={toggle}
          icon={<MessageOutlined />}
          disabled={!commentsCount}
        />
      </Badge>
      {open && (
        <DraggableModal
          title={title}
          subTitle={subtitle}
          isOpen={open}
          toggle={toggle}
          footer={false}
          titleClassName={s['modal-title']}
          withStackOffset
          subHeader={
            <div className={s['sub-header']}>
              <ReadingCell
                values={values}
                statuses={statuses}
                readingType={readingType}
              />
              <ThresholdCell
                values={values}
                statuses={statuses}
                readingType={readingType}
                globalStatus={globalStatus}
                thresholds={thresholds}
                thresholdName={thresholdName}
              />
            </div>
          }
        >
          <CommentList
            groupedComments={groupedComments}
            patientGuid={patientGuid}
          />
        </DraggableModal>
      )}
    </div>
  );
};
export default CommentsCell;
